import { useEffect, useRef, useState } from 'react';

import { Box, LinearProgress, SxProps, Theme, Typography, TypographyProps } from '@mui/material';
import Lottie from 'lottie-react';

type TimedProgressPageProps = Readonly<{
  title: string;
  animationData: unknown;
  durationMs: number;
  onComplete: () => void;
  /** Drop the bordered white card, leaving the animation and bar on the page background. */
  showCard?: boolean;
  titlePlacement?: 'top' | 'bottom';
  titleVariant?: TypographyProps['variant'];
}>;

/**
 * Full-page animation with a progress bar that fills over `durationMs`, then calls `onComplete`.
 * The bar is a fixed timer, not real work — it reports nothing about what is happening behind it.
 */
export const TimedProgressPage = ({
  title,
  animationData,
  durationMs,
  onComplete,
  showCard = true,
  titlePlacement = 'top',
  titleVariant = 'display-lg-semibold',
}: TimedProgressPageProps) => {
  const [progress, setProgress] = useState(0);
  // Held in a ref so an inline callback can't restart the timers on every progress tick.
  const onCompleteRef = useRef(onComplete);

  onCompleteRef.current = onComplete;

  useEffect(() => {
    const steps = 100;
    const timer = setInterval(() => {
      setProgress((current) => Math.min(current + 1, steps));
    }, durationMs / steps);
    const timeout = setTimeout(() => onCompleteRef.current(), durationMs);

    return () => {
      clearInterval(timer);
      clearTimeout(timeout);
    };
  }, [durationMs]);

  const titleNode = (
    <Box sx={titleSx}>
      <Typography variant={titleVariant}>{title}</Typography>
    </Box>
  );

  return (
    <Box sx={containerSx}>
      {titlePlacement === 'top' && titleNode}
      <Box sx={progressContainerSx(showCard)}>
        <Box sx={animationContainerSx}>
          <Lottie animationData={animationData} loop={true} style={animationStyle} />
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={linearProgressSx} />
      </Box>
      {titlePlacement === 'bottom' && titleNode}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  gap: backrs.spacing['spacing-4xl'],
  flexGrow: 1,
  width: '100%',
  maxWidth: '100%',
  '@media (max-width: 600px)': {
    justifyContent: 'flex-start',
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  textAlign: 'center',
  '@media (max-width: 600px)': {
    px: backrs.spacing['spacing-xl'],
    pt: backrs.spacing['spacing-4xl'],
  },
});

const progressContainerSx =
  (showCard: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: backrs.spacing['spacing-2xl'],
    minWidth: 0,
    ...(showCard
      ? {
          backgroundColor: backrs.colors['Colors/Base/White'],
          borderRadius: backrs.radius['radius-xl'],
          border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
          px: {
            xs: 0,
            sm: backrs.spacing['spacing-9xl'],
          },
        }
      : {}),
  });

const animationContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  height: '100%',
  width: '100%',
};

const animationStyle: React.CSSProperties = {
  maxHeight: '500px',
  height: '500px',
  width: 'auto',
};

const linearProgressSx: SxProps<Theme> = ({ backrs }) => ({
  width: '90%',
  height: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
  '& .MuiLinearProgress-bar': {
    backgroundColor: backrs.colors['Brand/500'],
  },
});
